import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Input, 
  message, 
  Modal, 
  Tag, 
  Tooltip,
  Switch,
  Dropdown,
  Menu,
  Select
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CopyOutlined,
  MoreOutlined,
  SearchOutlined,
  ExperimentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { RulingConfigurationManager } from './';
import { RulingConfigUtils } from '../../utils/rulingConfigUtils';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/apiService';

const { Search } = Input;

const ConfigurationListView = ({ 
  projectId = null,
  showCreateButton = true,
  onSelect = null,
  selectionMode = false
}) => {
  const [configurations, setConfigurations] = useState([]);
  const [filteredConfigurations, setFilteredConfigurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingConfiguration, setEditingConfiguration] = useState(null);
  const [selectedConfiguration, setSelectedConfiguration] = useState(null);
  
  const { getAuthToken } = useAuth();

  const loadConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      let response;
      if (projectId) {
        response = await apiService.getProjectRulingConfigurations(projectId, token);
      } else {
        response = await apiService.getRulingConfigurations(token);
      }
      
      const configurations = response.data || response.configurations || [];
      setConfigurations(configurations);
    } catch (error) {
      console.error('Failed to load configurations:', error);
      message.error('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  }, [projectId, getAuthToken]);

  useEffect(() => {
    loadConfigurations();
  }, [loadConfigurations]);

  useEffect(() => {
    // Filter configurations based on search text and status
    let filtered = configurations;

    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(config => config.is_active);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(config => !config.is_active);
    }

    // Apply search text filter
    if (searchText) {
      filtered = filtered.filter(config =>
        config.name.toLowerCase().includes(searchText.toLowerCase()) ||
        (config.description && config.description.toLowerCase().includes(searchText.toLowerCase()))
      );
    }

    setFilteredConfigurations(filtered);
  }, [configurations, searchText, statusFilter]);

  const handleCreate = () => {
    setIsCreateModalVisible(true);
  };

  const handleEdit = (configuration) => {
    setEditingConfiguration(configuration);
    setIsEditModalVisible(true);
  };

  const handleDelete = (configuration) => {
    Modal.confirm({
      title: 'Delete Configuration',
      content: `Are you sure you want to delete "${configuration.name}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          const token = getAuthToken();
          await apiService.deleteRulingConfiguration(
            configuration._id || configuration.id,
            token,
            projectId
          );
          message.success('Configuration deleted successfully');
          loadConfigurations();
        } catch (error) {
          console.error('Delete failed:', error);
          message.error('Failed to delete configuration');
        }
      }
    });
  };

  const handleClone = async (configuration) => {
    try {
      const token = getAuthToken();
      const newName = `${configuration.name} (Copy)`;
      
      await apiService.cloneRulingConfiguration(
        configuration._id || configuration.id,
        newName,
        token,
        projectId
      );
      
      message.success('Configuration cloned successfully');
      loadConfigurations();
    } catch (error) {
      console.error('Clone failed:', error);
      message.error('Failed to clone configuration');
    }
  };

  const handleActivate = async (configuration, active) => {
    try {
      const token = getAuthToken();
      
      if (active) {
        await apiService.activateRulingConfiguration(
          configuration._id || configuration.id,
          token,
          projectId
        );
        message.success('Configuration activated');
      } else {
        // To deactivate, we need to update the configuration
        const updatedConfig = { ...configuration, is_active: false };
        await apiService.updateRulingConfiguration(
          configuration._id || configuration.id,
          updatedConfig,
          token,
          projectId
        );
        message.success('Configuration deactivated');
      }
      
      loadConfigurations();
    } catch (error) {
      console.error('Activation failed:', error);
      message.error('Failed to update configuration status');
    }
  };

  const handleSave = (savedConfiguration) => {
    setIsCreateModalVisible(false);
    setIsEditModalVisible(false);
    setEditingConfiguration(null);
    loadConfigurations();
  };

  const handleRowClick = (record) => {
    if (selectionMode && onSelect) {
      setSelectedConfiguration(record);
      onSelect(record);
    }
  };

  const getActionMenu = (record) => (
    <Menu>
      <Menu.Item 
        key="edit" 
        icon={<EditOutlined />}
        onClick={() => handleEdit(record)}
      >
        Edit
      </Menu.Item>
      <Menu.Item 
        key="clone" 
        icon={<CopyOutlined />}
        onClick={() => handleClone(record)}
      >
        Clone
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item 
        key="delete" 
        icon={<DeleteOutlined />}
        danger
        onClick={() => handleDelete(record)}
      >
        Delete
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.description && (
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {record.description}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 100
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Tag color={record.is_active ? 'green' : 'default'}>
            {record.is_active ? 'Active' : 'Inactive'}
          </Tag>
          {record.is_default && (
            <Tag color="blue" size="small">Default</Tag>
          )}
        </Space>
      )
    },
    {
      title: 'Rules',
      key: 'rules',
      width: 150,
      render: (_, record) => {
        const ruleCount = Object.keys(record.rules || {}).length;
        const validationErrors = RulingConfigUtils.validateConfiguration(record);
        const hasErrors = validationErrors.length > 0;
        
        return (
          <Space>
            <Tag>{ruleCount} rule sets</Tag>
            {hasErrors ? (
              <Tooltip title={`Validation errors: ${validationErrors[0]}`}>
                <Tag color="red" size="small">Invalid</Tag>
              </Tooltip>
            ) : (
              <Tag color="green" size="small">Valid</Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Active',
      key: 'active_toggle',
      width: 80,
      render: (_, record) => (
        <Switch
          checked={record.is_active}
          onChange={(checked) => handleActivate(record, checked)}
          size="small"
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
          />
          <Dropdown
            overlay={getActionMenu(record)}
            trigger={['click']}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </Space>
      )
    }
  ];

  return (
    <>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExperimentOutlined style={{ color: '#8b5cf6' }} />
            <span>Ruling Configurations</span>
            <Tag size="small" color={filteredConfigurations.length === configurations.length ? 'default' : 'blue'}>
              {filteredConfigurations.length} / {configurations.length}
            </Tag>
            {projectId && (
              <Tag size="small">Project Scope</Tag>
            )}
          </div>
        }
        extra={
          <Space>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 120 }}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active Only' },
                { value: 'inactive', label: 'Inactive Only' }
              ]}
            />
            <Search
              placeholder="Search configurations..."
              allowClear
              style={{ width: 200 }}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {showCreateButton && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Create Configuration
              </Button>
            )}
          </Space>
        }
      >
        {(statusFilter !== 'all' || searchText) && (
          <div style={{ 
            marginBottom: 16, 
            padding: 12, 
            background: 'var(--bg-tertiary)', 
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border-primary)'
          }}>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
              Showing {filteredConfigurations.length} of {configurations.length} configurations
              {statusFilter !== 'all' && (
                <span> • Filter: <strong>{statusFilter === 'active' ? 'Active only' : 'Inactive only'}</strong></span>
              )}
              {searchText && (
                <span> • Search: <strong>"{searchText}"</strong></span>
              )}
            </div>
          </div>
        )}
        <Table
          columns={columns}
          dataSource={filteredConfigurations}
          loading={loading}
          rowKey={(record) => record._id || record.id}
          rowClassName={(record) => !record.is_active ? 'inactive-configuration' : ''}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: {
              cursor: selectionMode ? 'pointer' : 'default'
            }
          })}
          rowSelection={selectionMode ? {
            type: 'radio',
            selectedRowKeys: selectedConfiguration ? [selectedConfiguration._id || selectedConfiguration.id] : [],
            onSelect: (record) => {
              setSelectedConfiguration(record);
              if (onSelect) onSelect(record);
            }
          } : undefined}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} configurations`
          }}
        />
      </Card>

      {/* Create Configuration Modal */}
      <Modal
        title="Create Ruling Configuration"
        open={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        width="90%"
        style={{ top: 20 }}
        footer={null}
      >
        <RulingConfigurationManager
          projectId={projectId}
          onSave={handleSave}
          onCancel={() => setIsCreateModalVisible(false)}
          mode="create"
        />
      </Modal>

      {/* Edit Configuration Modal */}
      <Modal
        title="Edit Ruling Configuration"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingConfiguration(null);
        }}
        width="90%"
        style={{ top: 20 }}
        footer={null}
      >
        {editingConfiguration && (
          <RulingConfigurationManager
            initialConfiguration={editingConfiguration}
            projectId={projectId}
            onSave={handleSave}
            onCancel={() => {
              setIsEditModalVisible(false);
              setEditingConfiguration(null);
            }}
            mode="edit"
          />
        )}
      </Modal>
    </>
  );
};

export default ConfigurationListView;